import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ArrowLeft, Bot, Settings, Shield, Loader2, Save, ChevronDown, ChevronRight, AlertCircle, MessageSquare, Phone, Trash2 } from 'lucide-react';
import { apiService, AdminAgent, UpdateAgentRateLimitsRequest } from '../../services/api';
import { toast } from 'sonner';

type ActiveTab = 'sms' | 'rateLimits';

export function CustomerAgentConfigPage() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<ActiveTab>('sms');
  const [customerName, setCustomerName] = useState('Kunde');

  // ===== SMS Sender ID tab state =====
  const [smsAgents, setSmsAgents] = useState<AdminAgent[]>([]);
  const [smsLoading, setSmsLoading] = useState(true);
  const [smsError, setSmsError] = useState<string | null>(null);
  const [expandedSmsAgent, setExpandedSmsAgent] = useState<string | null>(null);
  const [smsForms, setSmsForms] = useState<Record<string, string>>({});
  const [savingSmsAgent, setSavingSmsAgent] = useState<string | null>(null);
  const [clearingSmsAgent, setClearingSmsAgent] = useState<string | null>(null);

  // ===== Rate Limits tab state =====
  const [chatbots, setChatbots] = useState<AdminAgent[]>([]);
  const [rateLimitsLoading, setRateLimitsLoading] = useState(true);
  const [rateLimitsError, setRateLimitsError] = useState<string | null>(null);
  const [expandedBot, setExpandedBot] = useState<string | null>(null);
  const [editForms, setEditForms] = useState<Record<string, UpdateAgentRateLimitsRequest>>({});
  const [savingBot, setSavingBot] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId) return;
    loadCustomerName();
    loadSmsAgents();
    loadChatbots();
  }, [customerId]);

  const loadCustomerName = async () => {
    try {
      const customer = await apiService.getCustomer(customerId!);
      setCustomerName(customer.name);
    } catch (err) {
      console.error('Failed to load customer:', err);
    }
  };

  // ===== SMS Sender ID tab logic =====

  const loadSmsAgents = async () => {
    setSmsLoading(true);
    setSmsError(null);
    try {
      const agents = await apiService.getAdminAgents({ customerId: customerId!, hasSmsHooks: true });
      setSmsAgents(agents);
    } catch (err) {
      console.error('Failed to load SMS agents:', err);
      setSmsError('Fehler beim Laden der Agents');
    } finally {
      setSmsLoading(false);
    }
  };

  const handleToggleExpandSms = (agentId: string) => {
    if (expandedSmsAgent === agentId) {
      setExpandedSmsAgent(null);
    } else {
      setExpandedSmsAgent(agentId);
      const agent = smsAgents.find(a => a.id === agentId);
      if (agent && smsForms[agentId] === undefined) {
        setSmsForms(prev => ({
          ...prev,
          [agentId]: agent.smsSenderId ?? '',
        }));
      }
    }
  };

  const handleSaveSmsAgent = async (agentId: string) => {
    setSavingSmsAgent(agentId);
    try {
      const value = smsForms[agentId] ?? '';
      const updated = await apiService.updateAgentSmsSenderId(agentId, value || null);
      setSmsAgents(prev => prev.map(a => (a.id === updated.id ? updated : a)));
      setSmsForms(prev => ({
        ...prev,
        [agentId]: updated.smsSenderId ?? '',
      }));
      toast.success('SMS Sender ID gespeichert');
    } catch (err) {
      console.error('Failed to save SMS sender ID:', err);
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingSmsAgent(null);
    }
  };

  const handleClearSmsAgent = async (agentId: string) => {
    setClearingSmsAgent(agentId);
    try {
      const updated = await apiService.updateAgentSmsSenderId(agentId, null);
      setSmsAgents(prev => prev.map(a => (a.id === updated.id ? updated : a)));
      setSmsForms(prev => ({ ...prev, [agentId]: '' }));
      toast.success('SMS Sender ID zurückgesetzt');
    } catch (err) {
      console.error('Failed to clear SMS sender ID:', err);
      toast.error('Fehler beim Zurücksetzen');
    } finally {
      setClearingSmsAgent(null);
    }
  };

  // ===== Rate Limits tab logic (1:1 from CustomerChatbotRateLimitsPage) =====

  const loadChatbots = async () => {
    setRateLimitsLoading(true);
    setRateLimitsError(null);
    try {
      const agents = await apiService.getAdminAgents({ customerId: customerId!, type: 'chat' });
      setChatbots(agents);
    } catch (err) {
      console.error('Failed to load chatbots:', err);
      setRateLimitsError('Fehler beim Laden der Chatbots');
    } finally {
      setRateLimitsLoading(false);
    }
  };

  const handleToggleExpand = (agentId: string) => {
    if (expandedBot === agentId) {
      setExpandedBot(null);
    } else {
      setExpandedBot(agentId);
      const agent = chatbots.find(a => a.id === agentId);
      if (agent && !editForms[agentId]) {
        setEditForms(prev => ({
          ...prev,
          [agentId]: {
            messageRateLimit: agent.rateLimitConfig?.messageRateLimit ?? undefined,
            sessionCreationLimit: agent.rateLimitConfig?.sessionCreationLimit ?? undefined,
            concurrentSessionLimit: agent.rateLimitConfig?.concurrentSessionLimit ?? undefined,
          },
        }));
      }
    }
  };

  const updateFormField = (agentId: string, field: keyof UpdateAgentRateLimitsRequest, value: string) => {
    setEditForms(prev => ({
      ...prev,
      [agentId]: {
        ...prev[agentId],
        [field]: value ? parseInt(value) : undefined,
      },
    }));
  };

  const handleSave = async (agentId: string) => {
    const form = editForms[agentId];
    if (!form) return;

    setSavingBot(agentId);
    try {
      const updated = await apiService.updateAgentRateLimits(agentId, form);
      setChatbots(prev => prev.map(a => (a.id === updated.id ? updated : a)));
      // Update form with saved values
      setEditForms(prev => ({
        ...prev,
        [agentId]: {
          messageRateLimit: updated.rateLimitConfig?.messageRateLimit ?? undefined,
          sessionCreationLimit: updated.rateLimitConfig?.sessionCreationLimit ?? undefined,
          concurrentSessionLimit: updated.rateLimitConfig?.concurrentSessionLimit ?? undefined,
        },
      }));
      toast.success('Rate-Limit-Einstellungen gespeichert');
    } catch (err) {
      console.error('Failed to save rate limits:', err);
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingBot(null);
    }
  };

  const handleViewViolationLogs = (agentId: string) => {
    const agent = chatbots.find(a => a.id === agentId);
    navigate(`/rate-limit-logs?agentId=${agentId}`, {
      state: { agentName: agent?.name, customerId, customerName },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/customers')}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Zurück
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-6 h-6 text-blue-600" />
            Agent Konfiguration
          </h1>
          <p className="text-sm text-gray-500">Kunde: {customerName}</p>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <Button variant={activeTab === 'sms' ? 'default' : 'outline'} onClick={() => setActiveTab('sms')}>
          SMS Sender ID
        </Button>
        <Button variant={activeTab === 'rateLimits' ? 'default' : 'outline'} onClick={() => setActiveTab('rateLimits')}>
          Rate Limits
        </Button>
      </div>

      {/* ===== SMS Sender ID Tab ===== */}
      {activeTab === 'sms' && (
        <>
          {smsLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Lade Agents...</span>
            </div>
          ) : smsError ? (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="py-8 text-center">
                <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <p className="text-red-700">{smsError}</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={loadSmsAgents}>
                  Erneut versuchen
                </Button>
              </CardContent>
            </Card>
          ) : smsAgents.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Bot className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Keine Agents mit SMS-Hooks gefunden</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                {smsAgents.length} Agent{smsAgents.length !== 1 ? 's' : ''} mit SMS-Hooks gefunden.
                Lassen Sie das Feld leer, um den Standard (CHATBOT) zu verwenden.
              </p>

              {smsAgents.map((agent) => (
                <Card key={agent.id} className="border border-gray-200">
                  <CardHeader
                    className="cursor-pointer py-4 hover:bg-gray-50 transition-colors"
                    onClick={() => handleToggleExpandSms(agent.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {expandedSmsAgent === agent.id ? (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                        <Bot className="w-5 h-5 text-purple-600" />
                        <div>
                          <div className="font-medium text-gray-900">{agent.name}</div>
                          <div className="text-xs text-gray-500">
                            Sender ID: {agent.smsSenderId ? agent.smsSenderId : 'Standard (CHATBOT)'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {agent.type === 'chat' ? (
                            <><MessageSquare className="w-3 h-3 mr-1" />Chat</>
                          ) : (
                            <><Phone className="w-3 h-3 mr-1" />Voice</>
                          )}
                        </Badge>
                        <Badge variant={agent.isActive ? 'default' : 'secondary'}>
                          {agent.isActive ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>

                  {expandedSmsAgent === agent.id && (
                    <CardContent className="pt-0 pb-4 border-t">
                      <div className="mt-4 max-w-sm space-y-1">
                        <Label htmlFor={`sms-sender-${agent.id}`}>SMS Sender ID</Label>
                        <Input
                          id={`sms-sender-${agent.id}`}
                          type="text"
                          placeholder="Standard (CHATBOT)"
                          value={smsForms[agent.id] ?? ''}
                          onChange={(e) =>
                            setSmsForms(prev => ({ ...prev, [agent.id]: e.target.value }))
                          }
                        />
                      </div>
                      <div className="flex justify-end gap-2 mt-4">
                        {agent.smsSenderId && (
                          <Button
                            variant="outline"
                            onClick={() => handleClearSmsAgent(agent.id)}
                            disabled={savingSmsAgent === agent.id || clearingSmsAgent === agent.id}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            {clearingSmsAgent === agent.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4 mr-2" />
                            )}
                            Zurücksetzen
                          </Button>
                        )}
                        <Button
                          onClick={() => handleSaveSmsAgent(agent.id)}
                          disabled={savingSmsAgent === agent.id || clearingSmsAgent === agent.id}
                        >
                          {savingSmsAgent === agent.id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Speichern
                        </Button>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* ===== Rate Limits Tab ===== */}
      {activeTab === 'rateLimits' && (
        <>
          {rateLimitsLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Lade Chatbots...</span>
            </div>
          ) : rateLimitsError ? (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="py-8 text-center">
                <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <p className="text-red-700">{rateLimitsError}</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={loadChatbots}>
                  Erneut versuchen
                </Button>
              </CardContent>
            </Card>
          ) : chatbots.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Bot className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Keine Chatbots für diesen Kunden gefunden</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                {chatbots.length} Chatbot{chatbots.length !== 1 ? 's' : ''} gefunden.
                Lassen Sie ein Feld leer, um den globalen Standardwert zu verwenden.
              </p>

              {chatbots.map((agent) => (
                <Card key={agent.id} className="border border-gray-200">
                  <CardHeader
                    className="cursor-pointer py-4 hover:bg-gray-50 transition-colors"
                    onClick={() => handleToggleExpand(agent.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {expandedBot === agent.id ? (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                        <Bot className="w-5 h-5 text-purple-600" />
                        <div>
                          <div className="font-medium text-gray-900">{agent.name}</div>
                          <div className="text-xs text-gray-500">
                            {agent.rateLimitConfig
                              ? `Nachrichten: ${agent.rateLimitConfig.messageRateLimit ?? 'Standard'} · Sitzungen: ${agent.rateLimitConfig.sessionCreationLimit ?? 'Standard'} · Gleichzeitig: ${agent.rateLimitConfig.concurrentSessionLimit ?? 'Standard'}`
                              : 'Globale Standardwerte'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={agent.isActive ? 'default' : 'secondary'}>
                          {agent.isActive ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewViolationLogs(agent.id);
                          }}
                        >
                          <Shield className="w-4 h-4 mr-1" />
                          Logs
                        </Button>
                      </div>
                    </div>
                  </CardHeader>

                  {expandedBot === agent.id && (
                    <CardContent className="pt-0 pb-4 border-t">
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="space-y-1">
                          <Label htmlFor={`msg-${agent.id}`}>Nachrichtenrate (pro Minute)</Label>
                          <Input
                            id={`msg-${agent.id}`}
                            type="number"
                            min={1}
                            placeholder="Globaler Standard"
                            value={editForms[agent.id]?.messageRateLimit ?? ''}
                            onChange={(e) => updateFormField(agent.id, 'messageRateLimit', e.target.value)}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor={`sess-${agent.id}`}>Sitzungserstellung (pro Stunde)</Label>
                          <Input
                            id={`sess-${agent.id}`}
                            type="number"
                            min={1}
                            placeholder="Globaler Standard"
                            value={editForms[agent.id]?.sessionCreationLimit ?? ''}
                            onChange={(e) => updateFormField(agent.id, 'sessionCreationLimit', e.target.value)}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor={`conc-${agent.id}`}>Gleichzeitige Sitzungen</Label>
                          <Input
                            id={`conc-${agent.id}`}
                            type="number"
                            min={1}
                            placeholder="Globaler Standard"
                            value={editForms[agent.id]?.concurrentSessionLimit ?? ''}
                            onChange={(e) => updateFormField(agent.id, 'concurrentSessionLimit', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="flex justify-end mt-4">
                        <Button onClick={() => handleSave(agent.id)} disabled={savingBot === agent.id}>
                          {savingBot === agent.id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Speichern
                        </Button>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
